import { createFetchWithRetry, getCSRFToken } from '@koblink/api-utils'
import { getAuthToken, tryRefreshToken } from '../hooks/useAuth'
import { devLog } from './devLog'

/**
 * Pre-configured fetch client for KobSign Word Add-in.
 *
 * Uses token auth (Bearer header) when available, falling back to
 * session cookies for local development. Handles:
 * - 401: attempts token refresh, retries once, then triggers login prompt
 * - 429: retry with Retry-After header
 * - 5xx: retry with exponential backoff
 */

let onUnauthorized: (() => void) | null = null

/** Register a callback for when session expires (called by useAuth) */
export function setOnUnauthorized(callback: () => void) {
  onUnauthorized = callback
}

// fetchWithRetry handles 429 and 5xx retries. 401 is handled in apiFetch.
export const fetchWithRetry = createFetchWithRetry({})

// Prevent concurrent refresh attempts
let refreshInProgress: Promise<boolean> | null = null

/**
 * Fetch wrapper that includes auth token or CSRF + credentials.
 * All API calls should go through this function.
 *
 * On 401: tries to refresh the access token and retries the request once.
 * If refresh fails, calls the onUnauthorized callback (shows login prompt).
 */
export async function apiFetch(
  url: string,
  options: RequestInit = {},
): Promise<Response> {
  const response = await doFetch(url, options)

  // If 401/403 and not already a refresh call, try refreshing the token.
  // DRF returns 403 (not 401) when SessionAuthentication is in the auth chain
  // and no valid token/session is found (no WWW-Authenticate header → 403).
  // Skip refresh for feature gate 403s (upgrade_required) — those are not auth errors.
  let isFeatureGate = false
  if (response.status === 403) {
    try {
      const cloned = response.clone()
      const body = await cloned.json()
      if (body?.error === 'upgrade_required') {
        isFeatureGate = true
      }
    } catch { /* ignore parse errors */ }
  }
  const needsRefresh = (response.status === 401 || (response.status === 403 && !isFeatureGate))
    && !url.includes('/office/refresh-token')
  if (needsRefresh) {
    devLog.warn(`${response.status} received — attempting token refresh`)

    // Deduplicate concurrent refresh attempts
    if (!refreshInProgress) {
      refreshInProgress = tryRefreshToken().finally(() => {
        refreshInProgress = null
      })
    }
    const refreshed = await refreshInProgress

    if (refreshed) {
      devLog.info('Token refreshed — retrying request')
      return doFetch(url, options)
    }

    // Refresh failed — session truly expired
    devLog.warn('Token refresh failed — session expired')
    onUnauthorized?.()
  }

  return response
}

/** Internal fetch with auth headers */
async function doFetch(url: string, options: RequestInit = {}): Promise<Response> {
  const headers = new Headers(options.headers)
  const token = getAuthToken()
  const method = (options.method || 'GET').toUpperCase()

  if (token) {
    headers.set('Authorization', `Bearer ${token}`)
    devLog.info('Auth', `Bearer token: ${token.substring(0, 20)}...`)
  } else {
    devLog.warn('Auth', 'No token — using cookie fallback')
    if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
      const csrfToken = getCSRFToken()
      if (csrfToken) {
        headers.set('X-CSRFToken', csrfToken)
      }
    }
  }

  if (!headers.has('Content-Type') && !(options.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json')
  }

  const bodyInfo = options.body instanceof FormData
    ? `FormData (${[...options.body.keys()].join(', ')})`
    : options.body
      ? `${String(options.body).substring(0, 100)}`
      : '(empty)'

  devLog.request(`${method} ${url}`, bodyInfo)

  try {
    const response = await fetchWithRetry(url, {
      ...options,
      headers,
      credentials: 'include',
    })

    devLog.response(`${response.status} ${response.statusText}`, `${method} ${url}`)
    return response
  } catch (error) {
    const msg = error instanceof Error ? error.message : String(error)
    devLog.error(`Network error: ${msg}`, `${method} ${url}`)
    throw error
  }
}
